import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Package, Plus, Edit3, Trash2, Search, X, AlertTriangle, ImagePlus, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { http, formatPrice, ADMIN_CATEGORIES, SIZES, SUBCATEGORIES } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import AdminTopBar from "@/components/AdminTopBar";

const EMPTY = {
  name: "", category: "Statues", subcategory: "", size: "", price: "", mrp: "",
  stock: "", images: [], tagline: "", description: "", specs: "",
};

const stockBadge = (s) =>
  s === 0
    ? "bg-[#fee2e2] text-[#b91c1c]"
    : s < 5
    ? "bg-[#fef9c3] text-[#a16207]"
    : "bg-[#dcfce7] text-[#15803d]";
const stockLabel = (s) => (s === 0 ? "Out of stock" : s < 5 ? "Low stock" : "In stock");

const AdminProducts = () => {
  const { token, isAdmin } = useAdmin();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [stockFilter, setStockFilter] = useState("All");
  const [modal, setModal] = useState(null); // null | {mode:'add'} | {mode:'edit', product}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [stockEdit, setStockEdit] = useState({});
  const [uploadingIdx, setUploadingIdx] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () =>
    http.get("/admin/me", auth).then(() =>
      http.get("/products").then((r) => {
        setProducts(r.data.products.map((p) => ({ ...p, stock: p.stock ?? 0 })));
        setLoading(false);
      })
    );

  useEffect(() => {
    if (isAdmin) load().catch(() => setLoading(false));
    // eslint-disable-next-line
  }, [isAdmin]);

  const stats = useMemo(() => ({
    total: products.length,
    low: products.filter((p) => p.stock > 0 && p.stock < 5).length,
    out: products.filter((p) => p.stock === 0).length,
    avg: products.length
      ? Math.round(products.reduce((s, p) => s + p.price, 0) / products.length)
      : 0,
  }), [products]);

  const filtered = products.filter((p) => {
    if (catFilter !== "All" && p.category !== catFilter) return false;
    if (stockFilter === "In Stock" && p.stock < 5) return false;
    if (stockFilter === "Low Stock" && !(p.stock > 0 && p.stock < 5)) return false;
    if (stockFilter === "Out of Stock" && p.stock !== 0) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const openAdd = () => { setForm(EMPTY); setModal({ mode: "add" }); };
  const openEdit = (p) => {
    const imgs = (p.images && p.images.length ? p.images : p.image_url ? [p.image_url] : []);
    setForm({
      name: p.name, category: p.category, subcategory: p.subcategory || "",
      size: p.size != null ? String(p.size) : "", price: String(p.price), mrp: String(p.mrp),
      stock: String(p.stock), images: imgs, tagline: p.tagline || "",
      description: p.description || "", specs: (p.specs || []).join("\n"),
    });
    setModal({ mode: "edit", product: p });
  };

  const openCopy = (p) => {
    const imgs = (p.images && p.images.length ? p.images : p.image_url ? [p.image_url] : []);
    setForm({
      name: `${p.name} (Copy)`, category: p.category, subcategory: p.subcategory || "",
      size: p.size != null ? String(p.size) : "", price: String(p.price), mrp: String(p.mrp),
      stock: String(p.stock ?? 0), images: imgs, tagline: p.tagline || "",
      description: p.description || "", specs: (p.specs || []).join("\n"),
    });
    setModal({ mode: "add", copiedFrom: p.id });
  };

  const onImagePick = async (idx, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
      return toast.error("Only JPG, PNG or WebP images are allowed");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/upload", fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm((fm) => {
        const images = [...fm.images];
        images[idx] = data.url;
        return { ...fm, images: images.filter(Boolean).slice(0, 3) };
      });
      toast.success(`Image ${idx + 1} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Image upload failed");
    } finally {
      setUploadingIdx(null);
    }
  };

  const removeImage = (idx) =>
    setForm((fm) => ({ ...fm, images: fm.images.filter((_, i) => i !== idx) }));

  const saveProduct = async (e) => {
    e.preventDefault();
    const price = parseInt(form.price, 10);
    const mrp = parseInt(form.mrp, 10);
    const stock = parseInt(form.stock, 10);
    if (!form.name.trim()) return toast.error("Please add a product name");
    if (!form.images[0]) return toast.error("Please upload at least one image (Image 1)");
    if (isNaN(price) || isNaN(mrp)) return toast.error("Enter valid prices");
    if (price > mrp) return toast.error("Price cannot exceed MRP");
    if (isNaN(stock) || stock < 0) return toast.error("Stock must be 0 or more");
    if (form.category === "Statues" && form.size && isNaN(Number(form.size)))
      return toast.error("Size must be in inches (a number)");

    const images = form.images.filter(Boolean).slice(0, 3);
    const payload = {
      name: form.name.trim(), category: form.category, price, mrp, stock,
      subcategory: form.subcategory.trim(), size: form.size.trim(),
      image_url: images[0], images,
      tagline: form.tagline.trim(),
      description: form.description.trim(),
      specs: form.specs.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    setSaving(true);
    try {
      if (modal.mode === "add") {
        await http.post("/admin/products", payload, auth);
        toast.success("Product created");
      } else {
        await http.put(`/admin/products/${modal.product.id}`, payload, auth);
        toast.success("Product updated");
      }
      setModal(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveStock = async (p) => {
    const val = parseInt(stockEdit[p.id] ?? "", 10);
    if (isNaN(val) || val < 0) return toast.error("Enter a valid stock count");
    try {
      await http.patch(`/admin/products/${p.id}/stock`, { stock: val }, auth);
      toast.success("Stock updated");
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, stock: val } : x)));
      setStockEdit((s) => ({ ...s, [p.id]: undefined }));
    } catch {
      toast.error("Failed to update stock");
    }
  };

  const doDelete = async () => {
    try {
      await http.delete(`/admin/products/${confirmDelete.id}`, auth);
      toast.success("Product deleted");
      setProducts((prev) => prev.filter((x) => x.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onCategoryChange = (e) => {
    const c = e.target.value;
    setForm((fm) => ({
      ...fm,
      category: c,
      subcategory: (SUBCATEGORIES[c] || []).includes(fm.subcategory) ? fm.subcategory : "",
      size:
        c === "Statues"
          ? /^\d+(\.\d+)?$/.test(fm.size) ? fm.size : ""
          : SIZES.includes(fm.size) ? fm.size : "Medium",
    }));
  };
  const inputCls = "w-full h-10 border border-[#e2e8f0] rounded-sm px-3 text-sm outline-none focus:border-[#C62828] bg-white";
  const isStatue = form.category === "Statues";

  return (
    <main className="min-h-[80vh] bg-[#f8fafc]" data-testid="admin-dashboard">
      <AdminTopBar />

      <div className="max-w-[1400px] mx-auto px-4 py-5 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Total Products", value: stats.total, cls: "text-[#0f172a]", testid: "stat-total" },
            { label: "Low Stock (<5)", value: stats.low, cls: "text-[#a16207]", testid: "stat-low" },
            { label: "Out of Stock", value: stats.out, cls: "text-[#b91c1c]", testid: "stat-out" },
            { label: "Avg. Price", value: formatPrice(stats.avg), cls: "text-[#15803d]", testid: "stat-avg" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-sm border border-[#e2e8f0] p-4" data-testid={s.testid}>
              <p className="text-xs text-[#64748b] uppercase tracking-wide">{s.label}</p>
              <p className={`font-head text-2xl font-bold mt-1 ${s.cls}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-sm border border-[#e2e8f0] p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products…"
              className={`${inputCls} pl-9`}
              data-testid="admin-product-search-input"
            />
          </div>
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className={`${inputCls} w-auto`} data-testid="admin-category-filter">
            <option>All</option>
            {ADMIN_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className={`${inputCls} w-auto`}>
            <option>All</option>
            <option>In Stock</option>
            <option>Low Stock</option>
            <option>Out of Stock</option>
          </select>
          <button
            onClick={openAdd}
            className="flex items-center gap-1.5 bg-[#C62828] text-white text-sm font-semibold px-4 h-10 rounded-sm hover:bg-[#A01F1F]"
            data-testid="admin-add-product-button"
          >
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-sm border border-[#e2e8f0] overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="admin-products-table">
            <thead>
              <tr className="bg-[#f1f5f9] text-[#475569] text-left">
                <th className="px-4 py-3 font-semibold">Product</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Price</th>
                <th className="px-4 py-3 font-semibold">Stock</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-14 text-[#64748b]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-14 text-[#64748b]">No products found.</td></tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id} className="border-t border-[#e2e8f0] hover:bg-slate-50" data-testid={`admin-product-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={p.image_url} alt="" className="w-11 h-11 object-contain rounded-sm bg-white border border-[#e2e8f0]" />
                        <span className="line-clamp-2 max-w-[280px] text-[#0f172a]">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#475569]">
                      {p.category}
                      {p.subcategory && <span className="block text-[11px] text-[#94a3b8]">{p.subcategory}</span>}
                      {p.size != null && p.size !== "" && (
                        <span className="block text-[11px] text-[#94a3b8]">
                          {/^\d+(\.\d+)?$/.test(String(p.size)) ? `${p.size}"` : p.size}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold">{formatPrice(p.price)}</span>
                      <span className="text-xs text-[#94a3b8] line-through ml-1.5">{formatPrice(p.mrp)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min="0"
                          value={stockEdit[p.id] ?? p.stock}
                          onChange={(e) => setStockEdit((s) => ({ ...s, [p.id]: e.target.value }))}
                          className="w-16 h-8 border border-[#e2e8f0] rounded-sm px-2 text-sm outline-none focus:border-[#C62828]"
                          data-testid={`admin-stock-input-${p.id}`}
                        />
                        {stockEdit[p.id] !== undefined && String(stockEdit[p.id]) !== String(p.stock) && (
                          <button
                            onClick={() => saveStock(p)}
                            className="text-xs font-semibold text-white bg-[#15803d] px-2.5 h-8 rounded-sm"
                            data-testid={`admin-stock-save-button-${p.id}`}
                          >
                            Save
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-sm ${stockBadge(p.stock)}`} data-testid={`admin-stock-badge-${p.id}`}>
                        {stockLabel(p.stock)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => openEdit(p)}
                          className="w-8 h-8 flex items-center justify-center rounded-sm border border-[#e2e8f0] text-[#C62828] hover:bg-[#FCEBEA]"
                          data-testid={`admin-edit-product-button-${p.id}`}
                          aria-label="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openCopy(p)}
                          className="w-8 h-8 flex items-center justify-center rounded-sm border border-[#e2e8f0] text-[#475569] hover:bg-slate-100"
                          data-testid={`admin-copy-product-button-${p.id}`}
                          aria-label="Duplicate"
                          title="Duplicate listing"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(p)}
                          className="w-8 h-8 flex items-center justify-center rounded-sm border border-[#e2e8f0] text-[#ef4444] hover:bg-red-50"
                          data-testid={`admin-delete-product-button-${p.id}`}
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit modal */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-product-modal">
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto fk-fade-up">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0] sticky top-0 bg-white">
              <h2 className="font-head text-lg font-bold text-[#0f172a]">
                {modal.mode === "add" ? (modal.copiedFrom ? "Duplicate Product" : "Add New Product") : "Edit Product"}
              </h2>
              <button onClick={() => setModal(null)} data-testid="admin-form-cancel-button" aria-label="Close">
                <X className="w-5 h-5 text-[#64748b]" />
              </button>
            </div>
            <form onSubmit={saveProduct} className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs text-[#64748b]">Product Name *</label>
                <input value={form.name} onChange={f("name")} className={inputCls} placeholder="e.g., Brass Ganesha Pooja Idol" data-testid="admin-form-name-input" />
              </div>
              <div>
                <label className="text-xs text-[#64748b]">Category *</label>
                <select value={form.category} onChange={onCategoryChange} className={inputCls} data-testid="admin-form-category-select">
                  {ADMIN_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#64748b]">Subcategory / Type</label>
                <select value={form.subcategory} onChange={f("subcategory")} className={inputCls} data-testid="admin-form-subcategory-input">
                  <option value="">Select type…</option>
                  {(SUBCATEGORIES[form.category] || []).map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                  {form.subcategory && !(SUBCATEGORIES[form.category] || []).includes(form.subcategory) && (
                    <option>{form.subcategory}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#64748b]">{isStatue ? "Size (inches)" : "Size"}</label>
                {isStatue ? (
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.size}
                    onChange={f("size")}
                    className={inputCls}
                    placeholder="e.g., 6"
                    data-testid="admin-form-size-input"
                  />
                ) : (
                  <select value={form.size} onChange={f("size")} className={inputCls} data-testid="admin-form-size-select">
                    {SIZES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                )}
              </div>
              <div>
                <label className="text-xs text-[#64748b]">Badge Tagline</label>
                <input value={form.tagline} onChange={f("tagline")} className={inputCls} placeholder="Deal of the Day" data-testid="admin-form-tagline-input" />
              </div>
              <div>
                <label className="text-xs text-[#64748b]">Selling Price (₹) *</label>
                <input type="number" min="0" value={form.price} onChange={f("price")} className={inputCls} placeholder="1499" data-testid="admin-form-price-input" />
              </div>
              <div>
                <label className="text-xs text-[#64748b]">MRP (₹) *</label>
                <input type="number" min="0" value={form.mrp} onChange={f("mrp")} className={inputCls} placeholder="2499" data-testid="admin-form-mrp-input" />
              </div>
              <div>
                <label className="text-xs text-[#64748b]">Stock Quantity *</label>
                <input type="number" min="0" value={form.stock} onChange={f("stock")} className={inputCls} placeholder="25" data-testid="admin-form-stock-input" />
              </div>

              {/* 3 image upload slots */}
              <div className="sm:col-span-2">
                <label className="text-xs text-[#64748b]">Product Images * (up to 3 — Image 1 is the main photo)</label>
                <div className="flex items-start gap-3 mt-1">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="relative">
                      <label
                        className="cursor-pointer block w-20 h-20 rounded-sm overflow-hidden"
                        data-testid={`admin-form-image-slot-${i}`}
                      >
                        {form.images[i] ? (
                          <img
                            src={form.images[i]}
                            alt={`Image ${i + 1}`}
                            className="w-full h-full object-contain bg-white border border-[#e2e8f0] rounded-sm"
                            data-testid={`admin-form-image-preview-${i}`}
                          />
                        ) : (
                          <div className="w-full h-full border border-dashed border-[#cbd5e1] rounded-sm flex flex-col items-center justify-center text-[#94a3b8] hover:border-[#C62828] hover:text-[#C62828] transition-colors">
                            <ImagePlus className="w-5 h-5" />
                            <span className="text-[10px] mt-0.5">
                              {uploadingIdx === i ? "Uploading…" : `Image ${i + 1}`}
                            </span>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => onImagePick(i, e)}
                          disabled={uploadingIdx !== null}
                          data-testid={`admin-form-image-upload-input-${i}`}
                        />
                      </label>
                      {form.images[i] && (
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-[#ef4444] text-white rounded-full flex items-center justify-center shadow"
                          data-testid={`admin-form-image-remove-${i}`}
                          aria-label={`Remove image ${i + 1}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-[#94a3b8] mt-1">JPG, PNG or WebP, up to 5 MB each. Click a filled image to replace it.</p>
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs text-[#64748b]">Description *</label>
                <textarea value={form.description} onChange={f("description")} rows={3} className={`${inputCls} h-auto py-2`} placeholder="Crafted by master artisans…" data-testid="admin-form-description-input" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-[#64748b]">Specifications (one per line)</label>
                <textarea value={form.specs} onChange={f("specs")} rows={3} className={`${inputCls} h-auto py-2`} placeholder={"Material: Solid Brass\nWeight: 850 grams"} data-testid="admin-form-specs-input" />
              </div>
              <div className="sm:col-span-2 flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setModal(null)} className="px-5 h-10 rounded-sm border border-[#e2e8f0] text-sm font-medium text-[#475569]">Cancel</button>
                <button type="submit" disabled={saving || uploadingIdx !== null} className="px-6 h-10 rounded-sm bg-[#C62828] text-white text-sm font-semibold hover:bg-[#A01F1F] disabled:opacity-60" data-testid="admin-form-save-button">
                  {saving ? "Saving…" : modal.mode === "add" ? "Create Product" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-delete-dialog">
          <div className="bg-white rounded-sm w-full max-w-sm p-5 fk-fade-up">
            <div className="flex items-center gap-2 text-[#b91c1c]">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-head font-bold">Delete product?</h3>
            </div>
            <p className="text-sm text-[#475569] mt-2 line-clamp-2">
              {confirmDelete.name} will be permanently removed from the catalog.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmDelete(null)} className="px-4 h-9 rounded-sm border border-[#e2e8f0] text-sm">Cancel</button>
              <button onClick={doDelete} className="px-4 h-9 rounded-sm bg-[#ef4444] text-white text-sm font-semibold hover:bg-[#dc2626]" data-testid="admin-delete-confirm-button">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Package className="hidden" />
    </main>
  );
};

export default AdminProducts;
